import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { 
    Star, 
    MessageCircle, 
    Send, 
    Loader2, 
    Trash2, 
    Edit, 
    X,
    User,
    Clock
} from 'lucide-react';
import { toast } from 'sonner';

// Star Rating Component
const StarRating = ({ value, onChange, readonly = false, size = 'md' }) => {
    const [hoverValue, setHoverValue] = useState(0);
    
    const sizeClasses = {
        sm: 'w-4 h-4',
        md: 'w-5 h-5',
        lg: 'w-6 h-6'
    };
    
    return (
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    type="button"
                    disabled={readonly}
                    onClick={() => !readonly && onChange && onChange(star)}
                    onMouseEnter={() => !readonly && setHoverValue(star)}
                    onMouseLeave={() => !readonly && setHoverValue(0)}
                    className={`${readonly ? 'cursor-default' : 'cursor-pointer'} transition-transform ${!readonly && 'hover:scale-110'}`}
                >
                    <Star 
                        className={`${sizeClasses[size]} ${
                            star <= (hoverValue || value)
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                        }`}
                    />
                </button>
            ))}
        </div>
    );
};

// Single Comment Component
const Comment = ({ comment, onUpdate, onDelete, currentUserId, isAdmin }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(comment.content);
    const [editRating, setEditRating] = useState(comment.rating);
    const [saving, setSaving] = useState(false);
    
    const canEdit = currentUserId === comment.user_id || isAdmin;
    
    const handleSave = async () => {
        if (!editContent.trim()) {
            toast.error('Le commentaire ne peut pas être vide');
            return;
        }
        setSaving(true);
        try {
            await onUpdate(comment.id, { content: editContent, rating: editRating });
            setIsEditing(false);
            toast.success('Commentaire modifié');
        } catch (error) {
            toast.error('Erreur lors de la modification');
        } finally {
            setSaving(false);
        }
    };
    
    const handleDelete = async () => {
        if (!window.confirm('Supprimer ce commentaire?')) return;
        try {
            await onDelete(comment.id);
            toast.success('Commentaire supprimé');
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };
    
    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-CA', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    if (isEditing) {
        return (
            <div className="bg-muted/50 rounded-2xl p-4 border-2 border-primary/30">
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-medium">Votre note:</span>
                    <StarRating value={editRating || 0} onChange={setEditRating} size="sm" />
                    {editRating && (
                        <button 
                            onClick={() => setEditRating(null)}
                            className="text-xs text-muted-foreground hover:text-foreground"
                        >
                            (retirer)
                        </button>
                    )}
                </div>
                <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="mb-3 rounded-xl"
                    rows={3}
                />
                <div className="flex gap-2 justify-end">
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                            setIsEditing(false);
                            setEditContent(comment.content);
                            setEditRating(comment.rating);
                        }}
                    >
                        <X className="w-4 h-4 mr-1" />
                        Annuler
                    </Button>
                    <Button 
                        size="sm" 
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                        Enregistrer
                    </Button>
                </div>
            </div>
        );
    }
    
    return (
        <div className="bg-white rounded-2xl p-4 border shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold">
                        {comment.user_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="font-semibold">{comment.user_name}</span>
                            {comment.rating && (
                                <StarRating value={comment.rating} readonly size="sm" />
                            )}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {formatDate(comment.created_at)}
                            {comment.updated_at && (
                                <span className="ml-1">(modifié)</span>
                            )}
                        </div>
                    </div>
                </div>
                
                {canEdit && (
                    <div className="flex gap-1">
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => setIsEditing(true)}
                        >
                            <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={handleDelete}
                        >
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </div>
                )}
            </div>
            
            <p className="mt-3 text-foreground leading-relaxed">{comment.content}</p>
        </div>
    );
};

// Rating Summary Component
const RatingSummary = ({ rating, onViewAll }) => {
    if (!rating || rating.count === 0) {
        return (
            <div className="text-center py-4 text-muted-foreground">
                <Star className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>Aucune note pour le moment</p>
                <p className="text-sm">Soyez le premier à noter cette recette!</p>
            </div>
        );
    }
    
    return (
        <div className="flex items-center gap-6">
            <div className="text-center">
                <div className="text-4xl font-bold text-foreground">{rating.average}</div>
                <StarRating value={Math.round(rating.average)} readonly size="md" />
                <div className="text-sm text-muted-foreground mt-1">
                    {rating.count} avis
                </div>
            </div>
            
            <div className="flex-1 space-y-1">
                {[5, 4, 3, 2, 1].map((star) => {
                    const count = rating.distribution[star] || 0;
                    const percent = rating.count > 0 ? (count / rating.count) * 100 : 0;
                    return (
                        <div key={star} className="flex items-center gap-2 text-sm">
                            <span className="w-3">{star}</span>
                            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-yellow-400 rounded-full transition-all"
                                    style={{ width: `${percent}%` }}
                                />
                            </div>
                            <span className="w-8 text-right text-muted-foreground">{count}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Main Comments Section Component
export const CommentsSection = ({ recipeId }) => {
    const { user, isAdmin } = useAuth();
    const [comments, setComments] = useState([]);
    const [rating, setRating] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(0);
    
    // New comment form
    const [newComment, setNewComment] = useState('');
    const [newRating, setNewRating] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    
    useEffect(() => {
        loadComments();
        loadRating();
    }, [recipeId]);
    
    const loadComments = async (skip = 0) => {
        try {
            if (skip === 0) setLoading(true);
            else setLoadingMore(true);
            
            const response = await api.getRecipeComments(recipeId, skip, 10);
            
            if (skip === 0) {
                setComments(response.data.comments);
            } else {
                setComments(prev => [...prev, ...response.data.comments]);
            }
            setTotal(response.data.total);
            setHasMore(response.data.has_more);
        } catch (error) {
            console.error('Error loading comments:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };
    
    const loadRating = async () => {
        try {
            const response = await api.getRecipeRating(recipeId);
            setRating(response.data);
        } catch (error) {
            console.error('Error loading rating:', error);
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!user) {
            toast.error('Connectez-vous pour commenter');
            return;
        }
        
        if (!newComment.trim()) {
            toast.error('Écrivez un commentaire');
            return;
        }
        
        setSubmitting(true);
        try {
            const response = await api.createComment(recipeId, {
                content: newComment.trim(),
                rating: newRating
            });
            
            setComments(prev => [response.data, ...prev]);
            setTotal(prev => prev + 1);
            setNewComment('');
            setNewRating(null);
            loadRating(); // Refresh rating
            toast.success('Commentaire ajouté!');
        } catch (error) {
            toast.error('Erreur lors de l\'ajout du commentaire');
        } finally {
            setSubmitting(false);
        }
    };
    
    const handleUpdate = async (commentId, data) => {
        const response = await api.updateComment(recipeId, commentId, data);
        setComments(prev => prev.map(c => c.id === commentId ? response.data : c));
        loadRating(); // Refresh rating
    };
    
    const handleDelete = async (commentId) => {
        await api.deleteComment(recipeId, commentId);
        setComments(prev => prev.filter(c => c.id !== commentId));
        setTotal(prev => prev - 1);
        loadRating(); // Refresh rating
    };
    
    return (
        <div className="space-y-6" data-testid="comments-section">
            {/* Rating Summary */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-3xl p-6 border">
                <h3 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    Notes & Avis
                </h3>
                <RatingSummary rating={rating} />
            </div>
            
            {/* New Comment Form */}
            {user ? (
                <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-6 border shadow-sm">
                    <h4 className="font-heading font-bold mb-4">Laisser un commentaire</h4>
                    
                    <div className="flex items-center gap-3 mb-4">
                        <span className="text-sm font-medium">Votre note (optionnel):</span>
                        <StarRating value={newRating || 0} onChange={setNewRating} />
                        {newRating && (
                            <button 
                                type="button"
                                onClick={() => setNewRating(null)}
                                className="text-xs text-muted-foreground hover:text-foreground"
                            >
                                (retirer)
                            </button>
                        )}
                    </div>
                    
                    <Textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Partagez votre expérience avec cette recette..."
                        className="mb-4 rounded-xl"
                        rows={3}
                        data-testid="comment-input"
                    />
                    
                    <div className="flex justify-end">
                        <Button 
                            type="submit" 
                            disabled={submitting || !newComment.trim()}
                            className="rounded-full"
                            data-testid="submit-comment"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Send className="w-4 h-4 mr-2" />
                            )}
                            Publier
                        </Button>
                    </div>
                </form>
            ) : (
                <div className="bg-muted/50 rounded-3xl p-6 text-center">
                    <MessageCircle className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-muted-foreground mb-3">Connectez-vous pour laisser un commentaire</p>
                    <Button variant="outline" asChild className="rounded-full">
                        <a href="/login">Se connecter</a>
                    </Button>
                </div>
            )}
            
            {/* Comments List */}
            <div>
                <h4 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-primary" />
                    Commentaires ({total})
                </h4>
                
                {loading ? (
                    <div className="text-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                    </div>
                ) : comments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                        <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>Aucun commentaire pour le moment</p>
                        <p className="text-sm">Soyez le premier à partager votre avis!</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {comments.map((comment) => (
                            <Comment
                                key={comment.id}
                                comment={comment}
                                onUpdate={handleUpdate}
                                onDelete={handleDelete}
                                currentUserId={user?.id}
                                isAdmin={isAdmin}
                            />
                        ))}
                        
                        {hasMore && (
                            <div className="text-center pt-4">
                                <Button
                                    variant="outline"
                                    onClick={() => loadComments(comments.length)}
                                    disabled={loadingMore}
                                    className="rounded-full"
                                >
                                    {loadingMore ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : null}
                                    Voir plus de commentaires
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export { StarRating };
export default CommentsSection;
